import { useEffect, useMemo, useState } from 'react';
import * as oiml from '../utils/oiml.js';

// Calculators (Joe, 20 Oct 2026): strength/density/temperature, dilution,
// blending and sugar effects (the AlcoDens set, built on the OIML R22
// alcoholometric tables in utils/oiml.js), plus Joe's liqueur fortification
// calculator ported with the same maths.

const fmt = (n, d) => Number(n).toLocaleString('en-GB', { minimumFractionDigits: d, maximumFractionDigits: d });
const sign = (n, d) => (n > 0 ? '+' : n < 0 ? '−' : '±') + fmt(Math.abs(n), d);

const DEFAULTS = { mode: 'hold', vol: '1000', abv: '34', sug: '300', sabv: '96.4', tabv: '40', tsug: '', k: '0.625' };
const STORE = 'blake_fortify';

/**
 * Raise a sweetened batch's strength with neutral spirit, either holding the
 * sugar level (spirit and sugar solved together) or letting it dilute.
 * Volumes are additive; dissolved sugar takes up k litres per kg.
 */
export function fortify({ mode, vol, abv, sug, sabv, tabv, tsug, k }) {
  const V = +vol; const a0 = +abv / 100; const c0 = +sug / 1000;
  const as = +sabv / 100; const at = +tabv / 100; const K = +k;
  const ct = mode === 'hold' ? (tsug === '' ? c0 : +tsug / 1000) : null;

  if (!(V > 0)) return { err: 'Enter the current batch volume.' };
  if (!(as > 0 && as <= 1)) return { err: "Enter the fortifying spirit's strength." };
  if (!(at > 0)) return { err: 'Enter a target strength.' };
  if (at <= a0) return { err: 'The target strength must be above the current strength. This calculator only fortifies; to reduce strength, dilute with water.' };
  if (at >= as) return { err: "The target strength must be below the spirit's strength." };

  const E = a0 * V; const S = c0 * V;
  let Vs = 0; let m = 0; let Vn = 0;
  if (mode === 'dilute') {
    Vs = (at * V - E) / (as - at);
    Vn = V + Vs;
  } else {
    const D = 1 - K * ct;
    if (D <= 0 || as - at / D <= 0) return { err: "These targets can't be reached: at that sugar level, sugar alone would take up too much of the volume for this spirit to hit the target strength." };
    Vs = (at * (V - K * S) / D - E) / (as - at / D);
    Vn = (V + Vs - K * S) / D;
    m = ct * Vn - S;
    if (m < 0) return { err: `The target sugar is below what spirit alone would dilute it to (${fmt(1000 * S / (V + (at * V - E) / (as - at)), 0)} g/L). Use "Let it dilute", or add water as well.` };
  }
  const En = E + as * Vs; const Sn = S + m;
  const sugVol = K * Sn;
  return {
    V, a0, c0, as, E, S, Vs, m, Vn, En, Sn, K,
    abvN: 100 * En / Vn, sugN: 1000 * Sn / Vn, sugVol, water: Vn - En - sugVol,
  };
}

const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg, 8px)', padding: 20 };
const label = { fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '0 0 10px' };
const mono = { fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontVariantNumeric: 'tabular-nums' };
const COLOURS = { ethanol: '#c98a3c', sugar: '#8a6a4a', water: '#7fa3b0' };

function NumField({ label: l, hint, unit, value, onChange, placeholder, step = 'any' }) {
  return (
    <label style={{ display: 'grid', gridTemplateColumns: '1fr 140px', alignItems: 'center', gap: 12, marginBottom: 10 }}>
      <span style={{ fontSize: 14 }}>{l}{hint && <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>{hint}</span>}</span>
      <span style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--border)', borderRadius: 'var(--radius, 6px)', background: 'var(--surface-2)' }}>
        <input type="number" inputMode="decimal" step={step} min="0" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
          style={{ ...mono, width: '100%', minWidth: 0, border: 0, background: 'transparent', color: 'var(--text)', fontSize: 14, padding: '8px 4px 8px 10px', textAlign: 'right', outline: 'none' }} />
        <b style={{ ...mono, fontWeight: 400, fontSize: 12, color: 'var(--text-muted)', paddingRight: 10, whiteSpace: 'nowrap' }}>{unit}</b>
      </span>
    </label>
  );
}

function FortificationCalculator() {
  const [f, setF] = useState(() => {
    try { return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(STORE) || 'null') || {}) }; } catch { return DEFAULTS; }
  });
  const [copied, setCopied] = useState('');
  useEffect(() => { try { localStorage.setItem(STORE, JSON.stringify(f)); } catch { /* not available */ } }, [f]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const r = useMemo(() => fortify(f), [f]);
  const hold = f.mode === 'hold';

  const sheet = r.err ? '' : [
    `Liqueur fortification (${hold ? 'sugar held' : 'sugar diluted'})`,
    `Start: ${fmt(r.V, 1)} L at ${fmt(r.a0 * 100, 2)}% ABV, ${fmt(r.c0 * 1000, 1)} g/L sugar`,
    `Add: ${fmt(r.Vs, 1)} L spirit at ${fmt(r.as * 100, 1)}% ABV${hold ? ` + ${fmt(r.m, 1)} kg sugar` : ''}`,
    `Finish: ${fmt(r.Vn, 1)} L at ${fmt(r.abvN, 2)}% ABV, ${fmt(r.sugN, 1)} g/L sugar, ${fmt(r.En, 1)} LPA`,
    `Sugar volume factor ${r.K} L/kg. Verify with a trial blend.`,
  ].join('\n');

  async function copy() {
    try { await navigator.clipboard.writeText(sheet); setCopied('Copied'); } catch { setCopied('Copy unavailable here'); }
    setTimeout(() => setCopied(''), 1600);
  }

  const modeBtn = (m, title, sub) => (
    <button type="button" onClick={() => set('mode')(m)} style={{
      padding: '10px 8px', textAlign: 'center', cursor: 'pointer', border: 0, fontSize: 14, fontWeight: 600, lineHeight: 1.25,
      background: f.mode === m ? 'var(--pld-green)' : 'var(--surface-2)', color: f.mode === m ? '#fff' : 'var(--text)',
    }}>{title}<small style={{ display: 'block', fontWeight: 400, fontSize: 12, opacity: 0.8 }}>{sub}</small></button>
  );

  const rows = r.err ? [] : [
    ['Volume', r.V, r.Vn, 1, ' L'], ['Strength', r.a0 * 100, r.abvN, 2, '%'], ['Sugar', r.c0 * 1000, r.sugN, 1, ' g/L'],
    ['Pure alcohol', r.E, r.En, 1, ' L'], ['Total sugar', r.S, r.Sn, 1, ' kg'],
  ];
  const pct = (x) => `${Math.max(0, (100 * x) / r.Vn)}%`;

  return (
    <div>
      <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Liqueur fortification</h2>
      <p style={{ margin: '0 0 18px', color: 'var(--text-muted)', fontSize: 14, maxWidth: '70ch' }}>
        Raise the strength of a sweetened batch with neutral spirit. Either hold the sugar level by adding sugar alongside the spirit, or let it dilute. Sugar's volume is counted, so there is no upper sugar limit.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 380px) minmax(0, 1fr)', gap: 20, alignItems: 'start' }} className="calc-grid">
        <div style={card}>
          <p style={label}>Sugar</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', border: '1px solid var(--border)', borderRadius: 'var(--radius, 6px)', overflow: 'hidden', marginBottom: 18 }}>
            {modeBtn('hold', 'Keep sugar level', 'adds spirit + sugar')}
            {modeBtn('dilute', 'Let it dilute', 'adds spirit only')}
          </div>
          <p style={label}>Current batch</p>
          <NumField label="Volume" unit="L" value={f.vol} onChange={set('vol')} />
          <NumField label="Strength" hint="true ABV, at 20 °C" unit="% ABV" value={f.abv} onChange={set('abv')} />
          <NumField label="Sugar" unit="g/L" value={f.sug} onChange={set('sug')} />
          <p style={{ ...label, marginTop: 16 }}>Fortifying spirit</p>
          <NumField label="Strength" unit="% ABV" value={f.sabv} onChange={set('sabv')} />
          <p style={{ ...label, marginTop: 16 }}>Target</p>
          <NumField label="Strength" unit="% ABV" value={f.tabv} onChange={set('tabv')} />
          {hold && <NumField label="Sugar" hint="defaults to current level" unit="g/L" value={f.tsug} placeholder={f.sug || '0'} onChange={set('tsug')} />}
          <details style={{ marginTop: 10 }}>
            <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>Sugar volume factor</summary>
            <NumField label="Volume per kg dissolved" hint="sucrose ≈ 0.625" unit="L/kg" step="0.005" value={f.k} onChange={set('k')} />
          </details>
          <button type="button" onClick={() => setF(DEFAULTS)} style={{ marginTop: 6, background: 'none', border: 0, padding: 0, color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>Reset to defaults</button>
        </div>

        <div style={{ ...card, display: 'grid', gap: 22 }} aria-live="polite">
          {r.err ? (
            <div style={{ borderRadius: 'var(--radius, 6px)', padding: '12px 14px', background: '#fdecea', color: '#9b2c1f', fontSize: 14 }}>{r.err}</div>
          ) : (<>
            <div>
              <p style={label}>Add to batch</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14 }}>
                <div style={{ borderTop: '3px solid var(--pld-green)', paddingTop: 10 }}>
                  <div style={{ ...mono, fontSize: 34, fontWeight: 500, lineHeight: 1.1 }}>{fmt(r.Vs, 1)}<small style={{ fontSize: 15, color: 'var(--text-muted)', marginLeft: 4 }}>L</small></div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>neutral spirit at {fmt(r.as * 100, 1)}% ABV · {fmt(r.as * r.Vs, 1)} LPA</div>
                </div>
                {hold && (
                  <div style={{ borderTop: '3px solid var(--pld-green)', paddingTop: 10 }}>
                    <div style={{ ...mono, fontSize: 34, fontWeight: 500, lineHeight: 1.1 }}>{fmt(r.m, 1)}<small style={{ fontSize: 15, color: 'var(--text-muted)', marginLeft: 4 }}>kg</small></div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>sugar (dry weight)</div>
                  </div>
                )}
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead><tr>{['Batch', 'Before', 'After', 'Change'].map((h, i) => <th key={h} style={{ ...label, margin: 0, padding: '8px 0', textAlign: i ? 'right' : 'left', borderBottom: '1px solid var(--border)' }}>{h}</th>)}</tr></thead>
                <tbody>
                  {rows.map(([name, b, a, d, u]) => (
                    <tr key={name}>
                      <td style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>{name}</td>
                      <td style={{ ...mono, padding: '8px 0', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>{fmt(b, d)}{u}</td>
                      <td style={{ ...mono, padding: '8px 0', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>{fmt(a, d)}{u}</td>
                      <td style={{ ...mono, padding: '8px 0', textAlign: 'right', borderBottom: '1px solid var(--border)', color: 'var(--pld-green)' }}>{sign(a - b, d)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <p style={label}>Final batch, by volume</p>
              <div style={{ display: 'flex', height: 26, borderRadius: 4, overflow: 'hidden', border: '1px solid var(--border)' }}>
                <span style={{ width: pct(r.En), background: COLOURS.ethanol }} />
                <span style={{ width: pct(r.sugVol), background: COLOURS.sugar }} />
                <span style={{ width: pct(r.water), background: COLOURS.water }} />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>
                {[['Ethanol', r.En, COLOURS.ethanol], ['Sugar', r.sugVol, COLOURS.sugar], ['Water', r.water, COLOURS.water]].map(([n, v, c]) => (
                  <span key={n}><i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 6, background: c }} />{n} <b style={{ ...mono, fontWeight: 500, color: 'var(--text)' }}>{fmt(v, 0)} L</b></span>
                ))}
              </div>
            </div>
            <button type="button" onClick={copy} style={{ justifySelf: 'start', fontSize: 13, fontWeight: 600, padding: '8px 14px', borderRadius: 'var(--radius, 6px)', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)', cursor: 'pointer' }}>
              {copied || 'Copy batch sheet'}
            </button>
          </>)}
          <div style={{ fontSize: 13, color: 'var(--text-muted)', display: 'grid', gap: 6, maxWidth: '72ch' }}>
            <p style={{ margin: 0 }}><strong style={{ color: 'var(--text)' }}>Method.</strong> Volumes are additive, with dissolved sugar taking up the volume factor per kg. In "keep" mode, spirit and sugar are solved together so both targets land at once.</p>
            <p style={{ margin: 0 }}><strong style={{ color: 'var(--text)' }}>Contraction.</strong> Ethanol and water shrink slightly on mixing, so the real batch comes in a touch under the calculated volume and a touch over on strength. Confirm with a trial blend and a true ABV check before duty-critical work.</p>
            <p style={{ margin: 0 }}><strong style={{ color: 'var(--text)' }}>Syrups.</strong> If you add sugar as syrup, its water is extra volume this doesn't account for. Use dry sugar, or adjust for the syrup's water.</p>
          </div>
        </div>
      </div>
      <style>{'@media (max-width: 860px) { .calc-grid { grid-template-columns: minmax(0, 1fr) !important; } }'}</style>
    </div>
  );
}

// ── Shared bits for the OIML calculators ──────────────────────────────────────
function usePersisted(key, defaults) {
  const [v, setV] = useState(() => {
    try { return { ...defaults, ...(JSON.parse(localStorage.getItem(key) || 'null') || {}) }; } catch { return defaults; }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* not available */ } }, [key, v]);
  return [v, setV, (k) => (x) => setV((o) => ({ ...o, [k]: x }))];
}

function Choice({ value, options, onChange }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${options.length}, 1fr)`, border: '1px solid var(--border)', borderRadius: 'var(--radius, 6px)', overflow: 'hidden', marginBottom: 18 }}>
      {options.map(([v, l, sub]) => (
        <button key={v} type="button" onClick={() => onChange(v)} style={{
          padding: '9px 6px', textAlign: 'center', cursor: 'pointer', border: 0, fontSize: 13, fontWeight: 600, lineHeight: 1.25,
          background: value === v ? 'var(--pld-green)' : 'var(--surface-2)', color: value === v ? '#fff' : 'var(--text)',
        }}>{l}{sub && <small style={{ display: 'block', fontWeight: 400, fontSize: 11, opacity: 0.8 }}>{sub}</small>}</button>
      ))}
    </div>
  );
}

function Big({ value, unit, note }) {
  return (
    <div style={{ borderTop: '3px solid var(--pld-green)', paddingTop: 10 }}>
      <div style={{ ...mono, fontSize: 32, fontWeight: 500, lineHeight: 1.1 }}>{value}<small style={{ fontSize: 14, color: 'var(--text-muted)', marginLeft: 4 }}>{unit}</small></div>
      {note && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{note}</div>}
    </div>
  );
}

function Rows({ rows }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
      <tbody>
        {rows.filter(Boolean).map(([l, v]) => (
          <tr key={l}>
            <td style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>{l}</td>
            <td style={{ ...mono, padding: '8px 0', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Layout({ title, intro, inputs, children, notes }) {
  return (
    <div>
      <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>{title}</h2>
      <p style={{ margin: '0 0 18px', color: 'var(--text-muted)', fontSize: 14, maxWidth: '70ch' }}>{intro}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 380px) minmax(0, 1fr)', gap: 20, alignItems: 'start' }} className="calc-grid">
        <div style={card}>{inputs}</div>
        <div style={{ ...card, display: 'grid', gap: 22 }} aria-live="polite">
          {children}
          {notes && <div style={{ fontSize: 13, color: 'var(--text-muted)', display: 'grid', gap: 6, maxWidth: '72ch' }}>{notes}</div>}
        </div>
      </div>
      <style>{'@media (max-width: 860px) { .calc-grid { grid-template-columns: minmax(0, 1fr) !important; } }'}</style>
    </div>
  );
}

const Err = ({ children }) => <div style={{ borderRadius: 'var(--radius, 6px)', padding: '12px 14px', background: '#fdecea', color: '#9b2c1f', fontSize: 14 }}>{children}</div>;
const Note = ({ title, children }) => <p style={{ margin: 0 }}><strong style={{ color: 'var(--text)' }}>{title}.</strong> {children}</p>;
const OIML_NOTE = <Note title="Basis">OIML R22 International Alcoholometric Tables (the basis of HMRC's and AlcoDens's tables). Strengths are true % vol at 20 °C; volumes are at 20 °C unless stated.</Note>;
const n = (v) => (v === '' || v == null ? NaN : Number(v));
const ok = (...v) => v.every((x) => Number.isFinite(x));

// ── Strength, density and temperature ─────────────────────────────────────────
function StrengthCalculator() {
  const [f, setF, set] = usePersisted('blake_strength', { mode: 'reading', value: '40', temp: '15', vol: '' });
  const t = n(f.temp); const v = n(f.value); const vol = n(f.vol);
  let r = null; let err = '';
  if (!ok(t) || t < -20 || t > 40) err = 'Enter a temperature between −20 and 40 °C.';
  else if (!ok(v)) err = 'Enter a value.';
  else {
    let abv;
    if (f.mode === 'true') abv = v;
    else if (f.mode === 'reading') abv = oiml.trueAbvFromReading(v, t);
    else abv = oiml.abvFromP(oiml.pFromRho(v, t));
    if (f.mode === 'density' && (v < oiml.rho(1, t) - 0.01 || v > oiml.rho(0, t) + 0.01)) err = `At ${t} °C an ethanol–water mix is between ${fmt(oiml.rho(1, t), 2)} and ${fmt(oiml.rho(0, t), 2)} kg/m³. A denser liquid has something else in it (e.g. sugar — use the Sugar & density calculator).`;
    else if (!(abv >= 0 && abv <= 100)) err = 'That reading is outside 0–100% vol.';
    else r = { abv, ...oiml.properties(abv, t), reading: oiml.readingFromTrueAbv(abv, t) };
  }
  const vol20 = r && ok(vol) && vol > 0 ? (vol * r.rhoT) / r.rho20 : null;
  return (
    <Layout title="Strength, density & temperature"
      intro="Convert a hydrometer reading or density at any temperature to true strength at 20 °C, with mass strength, proof, and the volume corrected to 20 °C."
      inputs={<>
        <p style={label}>I have</p>
        <Choice value={f.mode} onChange={set('mode')} options={[['reading', 'Hydrometer', 'reading at temp'], ['density', 'Density', 'at temp'], ['true', 'True ABV', 'at 20 °C']]} />
        <NumField label={f.mode === 'density' ? 'Density' : f.mode === 'reading' ? 'Hydrometer reading' : 'True strength'} hint={f.mode === 'reading' ? 'apparent % vol' : null}
          unit={f.mode === 'density' ? 'kg/m³' : '% vol'} value={f.value} onChange={set('value')} />
        <NumField label="Temperature" hint={f.mode === 'true' ? 'for density / reading at temp' : 'of the liquid'} unit="°C" value={f.temp} onChange={set('temp')} />
        <p style={{ ...label, marginTop: 16 }}>Volume (optional)</p>
        <NumField label="Volume measured" hint="at that temperature" unit="L" value={f.vol} onChange={set('vol')} />
      </>}
      notes={<>{OIML_NOTE}<Note title="Hydrometer">Readings assume a glass alcoholometer calibrated at 20 °C (OIML), with the glass's expansion allowed for.</Note></>}>
      {err ? <Err>{err}</Err> : (<>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14 }}>
          <Big value={fmt(r.abv, 2)} unit="% vol" note="true strength at 20 °C" />
          <Big value={fmt(r.abw, 2)} unit="% mass" note="alcohol by weight" />
          {vol20 != null && <Big value={fmt(vol20, 2)} unit="L" note={`at 20 °C · ${fmt(vol20 * r.abv / 100, 2)} LPA`} />}
        </div>
        <Rows rows={[
          ['Density at 20 °C', `${fmt(r.rho20, 2)} kg/m³`],
          t !== 20 ? [`Density at ${t} °C`, `${fmt(r.rhoT, 2)} kg/m³`] : null,
          ['Specific gravity (20/20 °C)', fmt(r.sg, 5)],
          t !== 20 ? [`Hydrometer would read at ${t} °C`, `${fmt(r.reading, 2)} % vol`] : null,
          ['UK proof', `${fmt(r.ukProof, 1)}°`],
          ['US proof (2 × ABV)', `${fmt(r.usProof, 1)}°`],
          ['Litres of pure alcohol per litre', fmt(r.lpaPerLitre, 4)],
        ]} />
      </>)}
    </Layout>
  );
}

// ── Dilution ──────────────────────────────────────────────────────────────────
function water(Mkg) { return Mkg / (oiml.RHO_WATER_20 / 1000); }
function DilutionCalculator() {
  const [f, setF, set] = usePersisted('blake_dilute', { mode: 'have', vol: '100', abv: '96.4', target: '40', want: '1000' });
  const a1 = n(f.abv); const a2 = n(f.target);
  let r = null; let err = '';
  if (!ok(a1, a2) || a1 <= 0 || a1 > 100) err = "Enter the spirit's strength.";
  else if (!(a2 > 0 && a2 < a1)) err = "The target strength must be above 0 and below the spirit's strength.";
  else {
    const V1 = f.mode === 'have' ? n(f.vol) : (n(f.want) * a2) / a1;
    if (!(V1 > 0)) err = f.mode === 'have' ? 'Enter the volume of spirit.' : 'Enter the volume you want to make.';
    else {
      const p1 = oiml.pFromAbv(a1); const p2 = oiml.pFromAbv(a2);
      const M1 = (V1 * oiml.rho(p1, 20)) / 1000; const M2 = (M1 * p1) / p2;
      const Wm = M2 - M1; const Wv = water(Wm); const V2 = M2 / (oiml.rho(p2, 20) / 1000);
      r = { V1, Wv, Wm, V2, lpa: (V1 * a1) / 100, contraction: V1 + Wv - V2 };
    }
  }
  const sheet = r && `Reduce ${fmt(r.V1, 2)} L at ${fmt(a1, 2)}% vol with ${fmt(r.Wv, 2)} L water (${fmt(r.Wm, 2)} kg) → ${fmt(r.V2, 2)} L at ${fmt(a2, 2)}% vol, ${fmt(r.lpa, 2)} LPA. Volumes at 20 °C (OIML R22).`;
  return (
    <Layout title="Dilution"
      intro="How much water to bring a spirit down to a target strength, allowing for the contraction when alcohol and water mix."
      inputs={<>
        <Choice value={f.mode} onChange={set('mode')} options={[['have', 'I have', 'a volume of spirit'], ['want', 'I want', 'a volume at target']]} />
        <p style={label}>Spirit</p>
        {f.mode === 'have' && <NumField label="Volume" unit="L" value={f.vol} onChange={set('vol')} />}
        <NumField label="Strength" unit="% vol" value={f.abv} onChange={set('abv')} />
        <p style={{ ...label, marginTop: 16 }}>Target</p>
        <NumField label="Strength" unit="% vol" value={f.target} onChange={set('target')} />
        {f.mode === 'want' && <NumField label="Volume to make" unit="L" value={f.want} onChange={set('want')} />}
      </>}
      notes={<>{OIML_NOTE}<Note title="Water">Add by weight where you can — it doesn't depend on temperature. Check the final strength before bottling or duty-critical work.</Note></>}>
      {err ? <Err>{err}</Err> : (<>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14 }}>
          {f.mode === 'want' && <Big value={fmt(r.V1, 2)} unit="L" note={`spirit at ${fmt(a1, 1)}% vol`} />}
          <Big value={fmt(r.Wv, 2)} unit="L" note={`water · ${fmt(r.Wm, 2)} kg`} />
          <Big value={fmt(r.V2, 2)} unit="L" note={`final at ${fmt(a2, 2)}% vol`} />
        </div>
        <Rows rows={[
          ['Spirit', `${fmt(r.V1, 2)} L`], ['Water', `${fmt(r.Wv, 2)} L (${fmt(r.Wm, 2)} kg)`], ['Final volume', `${fmt(r.V2, 2)} L`],
          ['Contraction', `${fmt(r.contraction, 2)} L`], ['Pure alcohol', `${fmt(r.lpa, 2)} LPA`],
        ]} />
        <CopyButton text={sheet} />
      </>)}
    </Layout>
  );
}

function CopyButton({ text }) {
  const [c, setC] = useState('');
  return (
    <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(text); setC('Copied'); } catch { setC('Copy unavailable here'); } setTimeout(() => setC(''), 1600); }}
      style={{ justifySelf: 'start', fontSize: 13, fontWeight: 600, padding: '8px 14px', borderRadius: 'var(--radius, 6px)', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)', cursor: 'pointer' }}>
      {c || 'Copy batch sheet'}
    </button>
  );
}

// ── Blending ──────────────────────────────────────────────────────────────────
function BlendCalculator() {
  const [f, setF, set] = usePersisted('blake_blend', { mode: 'have', va: '500', aa: '45', ab: '38', target: '40', want: '1000' });
  const aa = n(f.aa); const ab = n(f.ab); const at = n(f.target);
  let r = null; let err = '';
  if (!ok(aa, ab) || aa < 0 || ab < 0 || aa > 100 || ab > 100) err = 'Enter both strengths.';
  else if (aa === ab) err = 'The two spirits need different strengths.';
  else if (!(at > Math.min(aa, ab) && at < Math.max(aa, ab))) err = `The target must be between the two strengths (${fmt(Math.min(aa, ab), 1)}–${fmt(Math.max(aa, ab), 1)}% vol).`;
  else {
    const pa = oiml.pFromAbv(aa); const pb = oiml.pFromAbv(ab); const pt = oiml.pFromAbv(at);
    const ra = oiml.rho(pa, 20) / 1000; const rb = oiml.rho(pb, 20) / 1000; const rt = oiml.rho(pt, 20) / 1000;
    let MA; let MB;
    if (f.mode === 'have') {
      const VA = n(f.va);
      if (!(VA > 0)) err = 'Enter the volume of spirit A.';
      else { MA = VA * ra; MB = (MA * (pa - pt)) / (pt - pb); }
    } else {
      const Vt = n(f.want);
      if (!(Vt > 0)) err = 'Enter the volume you want to make.';
      else { const Mt = Vt * rt; MA = (Mt * (pt - pb)) / (pa - pb); MB = Mt - MA; }
    }
    if (!err) {
      const VA = MA / ra; const VB = MB / rb; const Vt = (MA + MB) / rt;
      r = { VA, VB, Vt, MA, MB, contraction: VA + VB - Vt, lpa: (Vt * at) / 100 };
    }
  }
  const sheet = r && `Blend ${fmt(r.VA, 2)} L at ${fmt(aa, 2)}% vol with ${fmt(r.VB, 2)} L at ${fmt(ab, 2)}% vol → ${fmt(r.Vt, 2)} L at ${fmt(at, 2)}% vol, ${fmt(r.lpa, 2)} LPA. Volumes at 20 °C (OIML R22).`;
  return (
    <Layout title="Blending two spirits"
      intro="Mix two spirits of different strengths to hit a target strength — either from a volume of one you already have, or to make a set volume."
      inputs={<>
        <Choice value={f.mode} onChange={set('mode')} options={[['have', 'I have', 'a volume of A'], ['want', 'I want', 'a volume at target']]} />
        <p style={label}>Spirit A</p>
        {f.mode === 'have' && <NumField label="Volume" unit="L" value={f.va} onChange={set('va')} />}
        <NumField label="Strength" unit="% vol" value={f.aa} onChange={set('aa')} />
        <p style={{ ...label, marginTop: 16 }}>Spirit B</p>
        <NumField label="Strength" unit="% vol" value={f.ab} onChange={set('ab')} />
        <p style={{ ...label, marginTop: 16 }}>Target</p>
        <NumField label="Strength" unit="% vol" value={f.target} onChange={set('target')} />
        {f.mode === 'want' && <NumField label="Volume to make" unit="L" value={f.want} onChange={set('want')} />}
      </>}
      notes={<>{OIML_NOTE}<Note title="Water">To reduce with water, use the Dilution calculator (or set spirit B to 0% vol).</Note></>}>
      {err ? <Err>{err}</Err> : (<>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14 }}>
          {f.mode === 'want' && <Big value={fmt(r.VA, 2)} unit="L" note={`spirit A at ${fmt(aa, 1)}% vol`} />}
          <Big value={fmt(r.VB, 2)} unit="L" note={`spirit B at ${fmt(ab, 1)}% vol`} />
          <Big value={fmt(r.Vt, 2)} unit="L" note={`blend at ${fmt(at, 2)}% vol`} />
        </div>
        <Rows rows={[
          ['Spirit A', `${fmt(r.VA, 2)} L (${fmt(r.MA, 2)} kg)`], ['Spirit B', `${fmt(r.VB, 2)} L (${fmt(r.MB, 2)} kg)`],
          ['Blend', `${fmt(r.Vt, 2)} L`], ['Contraction', `${fmt(r.contraction, 2)} L`], ['Pure alcohol', `${fmt(r.lpa, 2)} LPA`],
        ]} />
        <CopyButton text={sheet} />
      </>)}
    </Layout>
  );
}

// ── Sugar & density (obscuration) ─────────────────────────────────────────────
function SugarCalculator() {
  const [f, setF, set] = usePersisted('blake_sugar', { mode: 'true', abv: '40', density: '', reading: '', sugar: '4', k: '0.625', temp: '20' });
  const k = n(f.k);
  // "ABV + density": the sugar is what's worked out (Joe, 22 Oct 2026).
  const finding = f.mode === 'find';
  const S = finding ? oiml.sugarFromDensity(n(f.density), n(f.abv), ok(n(f.temp)) ? n(f.temp) : 20, k) : n(f.sugar);
  let r = null; let err = '';
  if (finding && (!ok(n(f.abv)) || !ok(n(f.density)))) err = 'Enter the true strength and the density.';
  else if (finding && !ok(S)) err = `That density doesn't fit ${f.abv}% ABV — at that strength with no sugar it would be ${fmt(oiml.liqueurDensity(n(f.abv), 0, k), 2)} kg/m³ at 20 °C, and sugar only makes it heavier. Check the ABV, the density and its temperature.`;
  else if (!ok(S) || S < 0) err = 'Enter the sugar content.';
  else if (!(k > 0 && k < 1)) err = 'Enter the sugar volume factor.';
  else {
    let abv;
    if (f.mode === 'true' || finding) abv = n(f.abv);
    else if (f.mode === 'density') abv = oiml.trueAbvFromDensity(n(f.density), S, k);
    else abv = oiml.trueAbvFromApparent(n(f.reading), S, k);
    if (!ok(abv) || abv < 0) err = f.mode === 'true' ? 'Enter the true strength.' : "Those figures don't fit together — check the reading and the sugar content.";
    else {
      const density = oiml.liqueurDensity(abv, S, k);
      if (!ok(density)) err = 'At that strength and sugar level the sugar would take up too much of the volume.';
      else {
        const apparent = oiml.apparentAbv(abv, S, k);
        r = { abv, density, apparent, obscuration: apparent == null ? null : abv - apparent };
      }
    }
  }
  return (
    <Layout title="Sugar & density"
      intro="Sugar makes a spirit denser, so a hydrometer reads it low (obscuration). Work out the true strength from density and sugar, or what a sweetened spirit will measure."
      inputs={<>
        <p style={label}>I have</p>
        <Choice value={f.mode} onChange={set('mode')} options={[['find', 'ABV + density', 'how much sugar?'], ['true', 'True ABV', 'what will it read?'], ['density', 'Density', 'true strength?'], ['reading', 'Hydrometer', 'true strength?']]} />
        {(f.mode === 'true' || finding) && <NumField label="True strength" hint={finding ? 'e.g. from distillation or the recipe' : null} unit="% vol" value={f.abv} onChange={set('abv')} />}
        {(f.mode === 'density' || finding) && <NumField label="Density" hint={finding ? 'measured' : 'at 20 °C'} unit="kg/m³" value={f.density} onChange={set('density')} placeholder="e.g. 1056.9" />}
        {finding && <NumField label="Temperature" hint="of the density reading" unit="°C" value={f.temp} onChange={set('temp')} />}
        {f.mode === 'reading' && <NumField label="Hydrometer reading" hint="apparent, at 20 °C" unit="% vol" value={f.reading} onChange={set('reading')} />}
        {!finding && <NumField label="Sugar" hint="dissolved solids" unit="g/L" value={f.sugar} onChange={set('sugar')} />}
        <details style={{ marginTop: 10 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>Sugar volume factor</summary>
          <NumField label="Volume per kg dissolved" hint="sucrose ≈ 0.625" unit="L/kg" step="0.005" value={f.k} onChange={set('k')} />
        </details>
      </>}
      notes={<>{OIML_NOTE}
        <Note title="Model">Sugar is treated as taking up its volume factor per kg, with the rest of each litre an ethanol–water mix — good for sucrose-sweetened spirits. Other solids (glycerol, fruit, colouring) differ.</Note>
        <Note title="Sugar from density">Approximate: everything dissolved that isn't alcohol or water — sugar, glycerine, fruit solids, acids — counts as sugar. Good for sugar-sweetened liqueurs; the true ABV needs to be right (each 1% out on the ABV moves the answer by about 5 g/L).</Note>
        <Note title="Duty">For obscured products HMRC expects the strength to be established properly (e.g. by distillation or a density meter calibrated for the product). Use this as a guide.</Note></>}>
      {err ? <Err>{err}</Err> : (<>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14 }}>
          {finding && <Big value={fmt(S, 0)} unit="g/L" note={`approx. sugar · ${fmt(S / r.density * 100, 1)} g per 100 g`} />}
          <Big value={fmt(r.abv, 2)} unit="% vol" note="true strength" />
          <Big value={fmt(r.density, 2)} unit="kg/m³" note="density at 20 °C" />
          <Big value={r.apparent == null ? 'below 0' : fmt(r.apparent, 2)} unit={r.apparent == null ? '' : '% vol'} note="hydrometer would read (20 °C)" />
        </div>
        <Rows rows={[
          ['Obscuration', r.obscuration == null ? 'Off the scale — denser than water, a hydrometer reads below 0' : `${fmt(r.obscuration, 2)} % vol`],
          ['Specific gravity (20/20 °C)', fmt(r.density / oiml.RHO_WATER_20, 5)],
          ['Sugar per bottle (70 cl)', `${fmt(S * 0.7, 1)} g`],
        ]} />
      </>)}
    </Layout>
  );
}

// ── Syrup + spirit (Joe, 27 Oct 2026) ─────────────────────────────────────────
// A syrup of known density mixed with a spirit (and water): what you get, or
// how much of each to hit a target strength and/or sugar. Amounts by volume
// or by weight (200 g of syrup into 200 ml of spirit).
const kgm3 = (v) => { const x = n(v); return ok(x) && x > 0 && x < 10 ? x * 1000 : x; }; // 1.33 kg/L → 1330 kg/m³
const UNITS = ['ml', 'L', 'g', 'kg'];
const isMass = (u) => u === 'g' || u === 'kg';
// Amount in its unit → litres, given the liquid's density in kg/m³.
const toLitres = (amt, unit, dens) => {
  const a = n(amt); if (!ok(a)) return NaN;
  if (unit === 'ml') return a / 1000;
  if (unit === 'L') return a;
  const kg = unit === 'g' ? a / 1000 : a;
  return dens > 0 ? kg / (dens / 1000) : NaN;
};
const fmtVol = (L) => (Math.abs(L) < 1 ? `${fmt(L * 1000, 0)} ml` : `${fmt(L, 2)} L`);
const fmtKg = (kg) => (Math.abs(kg) < 1 ? `${fmt(kg * 1000, 0)} g` : `${fmt(kg, 2)} kg`);
const bigVol = (L) => (Math.abs(L) < 1 ? [fmt(L * 1000, 0), 'ml'] : [fmt(L, 2), 'L']);

function AmountField({ label: l, hint, value, unit, onValue, onUnit, units = UNITS }) {
  return (
    <label style={{ display: 'grid', gridTemplateColumns: '1fr 140px', alignItems: 'center', gap: 12, marginBottom: 10 }}>
      <span style={{ fontSize: 14 }}>{l}{hint && <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>{hint}</span>}</span>
      <span style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--border)', borderRadius: 'var(--radius, 6px)', background: 'var(--surface-2)' }}>
        <input type="number" inputMode="decimal" step="any" min="0" value={value} onChange={(e) => onValue(e.target.value)}
          style={{ ...mono, width: '100%', minWidth: 0, border: 0, background: 'transparent', color: 'var(--text)', fontSize: 14, padding: '8px 4px 8px 10px', textAlign: 'right', outline: 'none' }} />
        <select value={unit} onChange={(e) => onUnit(e.target.value)} aria-label={`${l} unit`}
          style={{ ...mono, border: 0, background: 'transparent', color: 'var(--text-muted)', fontSize: 12, padding: '0 6px 0 0', cursor: 'pointer' }}>
          {units.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
      </span>
    </label>
  );
}

export function SyrupCalculator({ onSave }) {
  const [f, setF, set] = usePersisted('blake_syrup', { mode: 'mix', sd: '1330', st: '20', sl: '10', su: 'L', ab: '96', al: '10', au: 'L', wl: '0', wu: 'L', vol: '100', vu: 'L', ta: '25', ts: '250', k: '0.625' });
  const k = n(f.k); const d = kgm3(f.sd); const t = ok(n(f.st)) ? n(f.st) : 20;
  const syrupSugar = oiml.sugarFromDensity(d, 0, t, k);
  const syrupD20 = ok(syrupSugar) ? oiml.liqueurDensity(0, syrupSugar, k) : NaN; // kg/m³ at 20 °C
  const spiritD20 = n(f.ab) >= 0 && n(f.ab) <= 100 ? oiml.rho(oiml.pFromAbv(n(f.ab)), 20) : NaN;
  const waterD20 = oiml.RHO_WATER_20;
  let r = null; let plan = null; let err = ''; let L = null;
  if (!(k > 0 && k < 1)) err = 'Enter the sugar volume factor.';
  else if (!ok(d) || !ok(syrupSugar)) err = "Enter the syrup's density — it must be heavier than water (998.2 kg/m³ at 20 °C).";
  else if (f.mode === 'mix') {
    L = { syrup: toLitres(f.sl, f.su, syrupD20), spirit: toLitres(f.al, f.au, spiritD20), water: f.wl === '' ? 0 : toLitres(f.wl, f.wu, waterD20) };
    if (!(n(f.ab) >= 0 && n(f.ab) <= 100)) err = "Enter the spirit's strength.";
    else if (!(L.syrup >= 0) || !(L.spirit >= 0) || !(L.water >= 0)) err = 'Enter the amounts.';
    else {
      r = oiml.syrupBlend({ syrupL: L.syrup, syrupDensity: d, syrupTemp: t, spiritL: L.spirit, spiritAbv: n(f.ab), waterL: L.water, k });
      if (!r) err = "Those figures don't make a blend — check the amounts.";
    }
  } else if (!(n(f.ab) > 0 && n(f.ab) <= 100)) err = "Enter the spirit's strength.";
  else {
    const volL = f.vu === 'ml' ? n(f.vol) / 1000 : n(f.vol);
    plan = oiml.syrupTarget({ syrupDensity: d, syrupTemp: t, spiritAbv: n(f.ab), volume: volL, targetAbv: f.ta, targetSugar: f.ts, k });
    if (plan.error) { err = plan.error; plan = null; } else { r = plan.result; L = { syrup: plan.syrupL, spirit: plan.spiritL, water: plan.waterL }; }
  }
  const kg = L && { syrup: (L.syrup * syrupD20) / 1000, spirit: (L.spirit * spiritD20) / 1000, water: (L.water * waterD20) / 1000 };
  const brix = r ? (r.sugar / r.density) * 100 : null;
  const line = (name, Lx, kgx) => `${name} ${fmtVol(Lx)} (${fmtKg(kgx)})`;
  const sheet = r && `${line(`Syrup (${fmt(d, 1)} kg/m³, ~${fmt(syrupSugar, 0)} g/L sugar)`, L.syrup, kg.syrup)} + ${line(`spirit at ${fmt(n(f.ab), 1)}% vol`, L.spirit, kg.spirit)}${L.water > 0.0000005 ? ` + ${line('water', L.water, kg.water)}` : ''} → ${fmtVol(r.volume)} (${fmtKg(r.mass)}) at ${fmt(r.abv, 2)}% vol, ~${fmt(r.sugar, 0)} g/L sugar, ${fmt(r.density, 1)} kg/m³ (20 °C).`;
  // What gets saved to a project: each ingredient as entered (or worked out), and the result.
  const asEntered = (amt, unit, Lx) => (f.mode === 'mix' ? { amount: n(amt), unit } : { amount: Math.round(Lx * 1000) / 1000, unit: 'L' });
  const saveable = r && {
    syrup: asEntered(f.sl, f.su, L.syrup), spirit: asEntered(f.al, f.au, L.spirit),
    water: L.water > 0.0000005 ? asEntered(f.wl, f.wu, L.water) : null,
    syrupDensity: d, syrupSugar, spiritAbv: n(f.ab), result: r, sheet,
  };
  return (
    <Layout title="Syrup + spirit"
      intro="Blend a syrup of known density with a spirit (and water), by volume or by weight. See what you get, or work out how much of each to hit a target strength and/or sugar level."
      inputs={<>
        <Choice value={f.mode} onChange={set('mode')} options={[['mix', 'I have', 'amounts — what do I get?'], ['target', 'I want', 'a target — how much of each?']]} />
        <p style={label}>Syrup</p>
        <NumField label="Density" hint="kg/m³, or kg/L" unit="kg/m³" value={f.sd} onChange={set('sd')} placeholder="e.g. 1330 or 1.33" />
        <NumField label="Temperature" hint="of the density reading" unit="°C" value={f.st} onChange={set('st')} />
        {ok(syrupSugar) && syrupSugar > 0 && <p style={{ margin: '-4px 0 10px', fontSize: 12.5, color: 'var(--text-muted)' }}>≈ {fmt(syrupSugar, 0)} g/L sugar · {fmt(syrupSugar / syrupD20 * 100, 1)} °Brix</p>}
        {f.mode === 'mix' && <AmountField label="Amount" value={f.sl} unit={f.su} onValue={set('sl')} onUnit={set('su')} />}
        <p style={{ ...label, marginTop: 16 }}>Spirit</p>
        <NumField label="Strength" unit="% vol" value={f.ab} onChange={set('ab')} />
        {f.mode === 'mix' && <AmountField label="Amount" value={f.al} unit={f.au} onValue={set('al')} onUnit={set('au')} />}
        {f.mode === 'mix' ? (<>
          <p style={{ ...label, marginTop: 16 }}>Water</p>
          <AmountField label="Amount" hint="optional" value={f.wl} unit={f.wu} onValue={set('wl')} onUnit={set('wu')} />
        </>) : (<>
          <p style={{ ...label, marginTop: 16 }}>Target</p>
          <AmountField label="Volume to make" value={f.vol} unit={f.vu} onValue={set('vol')} onUnit={set('vu')} units={['ml', 'L']} />
          <NumField label="Strength" hint="leave blank if any" unit="% vol" value={f.ta} onChange={set('ta')} />
          <NumField label="Sugar" hint="leave blank if any" unit="g/L" value={f.ts} onChange={set('ts')} />
        </>)}
        <details style={{ marginTop: 10 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>Sugar volume factor</summary>
          <NumField label="Volume per kg dissolved" hint="sucrose ≈ 0.625" unit="L/kg" step="0.005" value={f.k} onChange={set('k')} />
        </details>
      </>}
      notes={<>{OIML_NOTE}
        <Note title="Approximate">The syrup's sugar is worked out from its density as if it were sugar and water, so anything else dissolved in it (acids, fruit solids, glycerine) counts as sugar. Measure the syrup's density at a known temperature, ideally 20 °C.</Note>
        <Note title="Weights">Weights are turned into volumes with each liquid's density at 20 °C: the syrup's from its density above, the spirit's from its strength, water 998.2 kg/m³.</Note>
        <Note title="Targets">With both a strength and a sugar target, water makes up the rest. With only one, there's no water and the other figure is whatever the mix gives.</Note>
        <Note title="Duty">Sweetened spirits are obscured: a hydrometer reads them low. Check the finished strength properly before declaring duty.</Note></>}>
      {err ? <Err>{err}</Err> : (<>
        {plan && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
            <Big value={bigVol(L.syrup)[0]} unit={bigVol(L.syrup)[1]} note={`syrup · ${fmtKg(kg.syrup)}`} />
            <Big value={bigVol(L.spirit)[0]} unit={bigVol(L.spirit)[1]} note={`spirit at ${fmt(n(f.ab), 1)}% vol · ${fmtKg(kg.spirit)}`} />
            <Big value={bigVol(L.water)[0]} unit={bigVol(L.water)[1]} note={`water · ${fmtKg(kg.water)}`} />
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
          <Big value={fmt(r.abv, 2)} unit="% vol" note="strength" />
          <Big value={fmt(r.sugar, 0)} unit="g/L" note={`approx. sugar · ${fmt(brix, 1)} g per 100 g`} />
          <Big value={bigVol(r.volume)[0]} unit={bigVol(r.volume)[1]} note={`you'll have · ${fmtKg(r.mass)}`} />
        </div>
        <Rows rows={[
          f.mode === 'mix' && ['Syrup', `${fmtVol(L.syrup)} · ${fmtKg(kg.syrup)}`],
          f.mode === 'mix' && ['Spirit', `${fmtVol(L.spirit)} · ${fmtKg(kg.spirit)}`],
          f.mode === 'mix' && L.water > 0 && ['Water', `${fmtVol(L.water)} · ${fmtKg(kg.water)}`],
          ['Density at 20 °C', `${fmt(r.density, 2)} kg/m³`],
          ['Hydrometer would read (20 °C)', r.apparent == null ? 'below 0 — denser than water' : `${fmt(r.apparent, 2)} % vol`],
          ['Sugar in the blend', fmtKg(r.sugarKg)],
          ['Pure alcohol', `${fmt(r.lpa, 3)} LPA`],
          ['Contraction', fmtVol(r.contraction)],
          ['Sugar per bottle (70 cl)', `${fmt(r.sugar * 0.7, 1)} g`],
        ]} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <CopyButton text={sheet} />
          {onSave && <button type="button" onClick={() => onSave(saveable)} style={{ padding: '8px 14px', borderRadius: 'var(--radius, 6px)', border: 'none', background: 'var(--pld-green)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Save to project…</button>}
        </div>
      </>)}
    </Layout>
  );
}

const TOOLS = [
  ['strength', 'Strength & temperature', StrengthCalculator],
  ['dilute', 'Dilution', DilutionCalculator],
  ['blend', 'Blending', BlendCalculator],
  ['sugar', 'Sugar & density', SugarCalculator],
  ['syrup', 'Syrup + spirit', SyrupCalculator],
  ['fortify', 'Liqueur fortification', FortificationCalculator],
];

export default function Calculators() {
  const [tool, setToolState] = useState(() => { try { return localStorage.getItem('blake_calc_tab') || TOOLS[0][0]; } catch { return TOOLS[0][0]; } });
  const setTool = (k) => { setToolState(k); try { localStorage.setItem('blake_calc_tab', k); } catch { /* not available */ } };
  const Tool = (TOOLS.find((t) => t[0] === tool) || TOOLS[0])[2];
  return (
    <div>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Calculators</h1>
      <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Blending and production sums</p>
      <div style={{ display: 'flex', gap: 4, margin: '16px 0 20px', borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
        {TOOLS.map(([k, l]) => (
          <button key={k} onClick={() => setTool(k)} style={{ whiteSpace: 'nowrap', padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', border: 'none', background: 'none', marginBottom: -1, color: tool === k ? 'var(--pld-green)' : 'var(--text-muted)', borderBottom: tool === k ? '2px solid var(--pld-green)' : '2px solid transparent' }}>{l}</button>
        ))}
      </div>
      <Tool />
    </div>
  );
}
